import React, { memo, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import { Avatar, Badge, Button, List, Popover, Typography } from 'antd';

import Truncate from 'components/Truncate';
import { dateMomentFormat } from 'v2/formatters/date';
import chatService from 'v2/services/chat';
import { ELITE_BADGES_URL } from 'settings';
import { Icon } from '@eduzz/ui-antd-theme';

interface IProps {
  isOpen: boolean;
  isFirstOpen: boolean;
  onOpenChange: (open: boolean) => void;
  unreadMessagesCount: number;
  children: React.ReactNode;
}

const MessagesList = memo(({ isOpen, onOpenChange, unreadMessagesCount, isFirstOpen, children }: IProps) => {
  const [chats] = useObservable(() => chatService.getMessages(), []);
  const [isLoading] = useObservable(() => chatService.getLoadingMessages(), []);

  const shouldLoadMessages = useMemo(() => {
    if (!isOpen) {
      return false;
    }

    if (isFirstOpen || unreadMessagesCount > 0) {
      return true;
    }

    return false;
  }, [isFirstOpen, isOpen, unreadMessagesCount]);

  useEffect(() => {
    if (shouldLoadMessages) {
      chatService.loadMessages(4);
    }
  }, [shouldLoadMessages]);

  const content = (
    <List
      className='min-w-70'
      header={
        <div className='flex justify-center'>
          <Typography.Text strong>Mensagens</Typography.Text>
        </div>
      }
      footer={
        <Link to='/producer/messages' onClick={() => onOpenChange(false)}>
          <Button id='see-all-messages-button' className='uppercase' type='link' block>
            Ver todas
          </Button>
        </Link>
      }
      locale={{ emptyText: 'Nenhuma mensagem disponível' }}
      dataSource={chats}
      renderItem={chat => (
        <List.Item key={chat.id}>
          <Link
            key={chat.id}
            id={`messages-${chat.id}-link`}
            to={'/producer/messages/' + chat.id}
            className={`flex items-center gap-7.5 p-2 hover:bg-(--uizz-antd-color-fill-content-hover) ${
              chat.not_read_count > 0 ? 'opacity-100' : 'opacity-60'
            }`}
            onClick={() => onOpenChange(false)}
          >
            <div className='relative'>
              <Badge dot={chat.not_read_count > 0} offset={[-34, 4]}>
                {chat.outer.avatar ? (<Avatar size='large' src={chat.outer.avatar} />) : (<Avatar size='large' icon={<Icon.User size={20} />} />)}
              </Badge>
              {chat.outer.qualification && (
                <div className='absolute inset-x-7.5 top-4.5'>
                  <Avatar
                    size='small'
                    src={`${ELITE_BADGES_URL}/${chat.outer.qualification.badge_elite}` + (chat.outer.qualification.has_plus ? '-plus' : '') + '.svg'}
                  />
                </div>
              )}
            </div>
            <div className='flex flex-col'>
              <Typography.Text strong>
                <Truncate count={25} final='...'>
                  {chat.outer.name}
                </Truncate>
              </Typography.Text>
              <Typography.Text type='secondary'>
                <Truncate count={25} final='...'>
                  {chat.last.content}
                </Truncate>
              </Typography.Text>
              <Typography.Text type='secondary'>{dateMomentFormat(chat.last.created_at)}</Typography.Text>
            </div>
          </Link>
        </List.Item>
      )}
    />
  );

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange} content={content} zIndex={9999} trigger='click'>
      {children}
    </Popover>
  );
});

export default MessagesList;
